import React from "react";
import { Flex, Text, AspectRatio } from "@chakra-ui/react";
import { useColorTheme } from "contexts/color-theme";
import { trackEvent } from "lib/analytics";
import { Link } from "ui/link";
import { DirectionalButton } from "ui/button/directional-button";
import { DecorativeBackgroundImage } from "components/images";
import { type ImageTeaserProps } from "./data";
import { NavColumnLabel } from "./nav-column-label";

export function ImageTeaser({ label, subLabel, image, href, ctaText }: ImageTeaserProps) {
  const { theme, bgColor } = useColorTheme();

  // Use the tertiary variant if the bgColor shade is .50, which only applies to light themes. Otherwise, use secondary.
  const buttonVariant = theme === "dark" || bgColor.includes("150") || bgColor === "white" ? "secondary" : "tertiary";

  const linkClicked = () => {
    trackEvent("Nav Item Clicked", {
      Label: `${label} | ${subLabel}`,
      Link: href,
    });
  };

  return (
    <Flex
      borderLeft="1px"
      borderColor={`brand.header.border.${theme}`}
      direction="column"
      flex={1}
      w="100%"
      mt="big"
      pl={{ base: "big", lg: "big" }}
      pr={{ base: "big", lg: "small", xl: 0 }}
    >
      <NavColumnLabel ml="0">{label}</NavColumnLabel>

      <Link
        aria-label={subLabel}
        href={href}
        className="group"
        variant="unstyled"
        flexDirection="column"
        alignItems="start"
        onClick={linkClicked}
      >
        {image && (
          <AspectRatio
            ratio={2 / 1}
            pos="relative"
            border="1px"
            borderColor={`brand.header.border.${theme}`}
            borderRadius="base"
            mt={1}
            w="100%"
            maxW="500px"
            overflow="hidden"
          >
            <DecorativeBackgroundImage src={`${image}?w=400`} />
          </AspectRatio>
        )}

        <Text textStyle="caption2" color={`brand.textTertiary.${theme}`} mt="smaller">
          {subLabel}
        </Text>

        <DirectionalButton as="span" size="sm" variant={buttonVariant} mt="tiny" pl={0}>
          {ctaText ?? "Learn more"}
        </DirectionalButton>
      </Link>
    </Flex>
  );
}
